import React, { useState } from 'react';

import { EditOutlined, PlusOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons';
import { cx } from '@emotion/css';
import { setSessionStorageItem } from '@revenuemarket/browser';
import { Flex } from '@revenuemarket/cds';
import { Colors, colorTokens, colors, theme } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { useDebounce } from '@revenuemarket/react';
import { KeyboardArrowDown } from '@revenuemarket/ui';
import { Button, Modal, Select, SelectProps, Tag, Typography } from 'antd';

import { BusinessEntity } from '~apis/business-entity';
import { BusinessEntityDetail } from '~apis/contract';
import BusinessEntityCreateModal from '~components/BusinessEntity/BusinessEntityCreateModal';
import useBusinessEntityListQuery from '~hooks/queries/useBusinessEntityListQuery';
import { useBusinessEntitySearchQuery } from '~hooks/queries/useBusinessEntityQuery';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import useSuperUserHook from '~hooks/useSuperUserHook';
import { css } from '~libs/emotion';

import { BUSINESS_ENTITY_RULE_LABEL_STORAGE_KEY } from './constant';

export const mapSearchOptions = (searchData?: BusinessEntity[]) => {
  return searchData?.map((d) => {
    return {
      label: d.name + ' / ' + d.businessRegNumber,
      value: d.id,
    };
  });
};

const SelectBusinessEntity = ({
  onChange,
  value,
  selectProps,
  defaultOpen,
}: {
  onChange: (value?: number, label?: string) => void;
  value?: number;
  selectProps?: Omit<SelectProps, 'value' | 'onChange' | 'onSearch'>;
  defaultOpen?: boolean;
}) => {
  const [hoveredItemId, setHoveredItemId] = useState<number | null>(null);
  const [modal, contextHolder] = Modal.useModal();
  const [search, setSearch] = useState('');
  const { companyId } = useDemoAwareCompanyId();
  const { refetch: refetchBusinessEntity } = useBusinessEntityListQuery({
    companyId: companyId as string,
    favoriteOnly: false,
  });
  const { data: businessEntityData } = useBusinessEntityListQuery({
    companyId: companyId as string,
    favoriteOnly: false,
  });

  const businessEntityLabelOptions =
    businessEntityData?.Data?.data?.map((b) => ({
      label: (
        <Flex
          align="center"
          gap={8}
          onMouseEnter={() => setHoveredItemId(b.id)}
          onMouseLeave={() => setHoveredItemId(null)}
        >
          <Typography.Text>{b.name}</Typography.Text>
          {hoveredItemId === b.id && (
            <EditOutlined
              onClick={(e) => {
                e.stopPropagation();
                overlays.open(({ isOpen, onClose }) => (
                  <BusinessEntityCreateModal
                    open={isOpen}
                    onCancel={onClose}
                    data={b as unknown as BusinessEntityDetail}
                  />
                ));
              }}
            />
          )}
        </Flex>
      ),
      value: b.id,
      data: b,
    })) || [];

  const filterOption = (
    input: string,
    option?: { label: string; value: number; data?: any },
  ): boolean => {
    const targetLabel =
      businessEntityData?.Data?.data?.find((d) => d.id === option?.value)?.name ?? '';
    return targetLabel.toLowerCase().includes(input.toLowerCase());
  };

  return (
    <>
      {contextHolder}
      <Select
        defaultOpen={defaultOpen}
        showSearch
        onSearch={setSearch}
        onChange={(value) => {
          const label = businessEntityData?.Data?.data?.find((d) => d.id === value)?.name || '';
          onChange(value as number, label);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Tab') {
            overlays.open(({ isOpen, onClose }) => (
              <BusinessEntityCreateModal
                open={isOpen}
                onCancel={onClose}
                name={search}
                onCreate={(id: number) => {
                  modal.confirm({
                    title: '거래처 분류 규칙을 만들어주세요',
                    content:
                      '직접 생성한 거래처는 자동으로 분류되지 않습니다. 새로운 거래처를 편리하게 활용하기 위해, 거래처 분류 규칙을 꼭 만들어주세요.',
                    okText: '규칙 설정으로 이동',
                    onOk: () => {
                      setSessionStorageItem(BUSINESS_ENTITY_RULE_LABEL_STORAGE_KEY, id);
                      window.open('/clobe/labeling', '_blank');
                    },
                  });
                }}
              />
            ));
          }
        }}
        notFoundContent={
          <Flex direction="column" gap={8} align="center">
            <Typography.Text
              style={{
                color: colorTokens.textDescription,
                fontWeight: 'bold',
              }}
            >
              검색 결과가 없습니다.
            </Typography.Text>
            <Flex direction="column" gap={4} align="center">
              <Flex align="center">
                <Tag>tab</Tag>
                <Typography.Text
                  style={{
                    color: colorTokens.textDescription,
                    margin: 0,
                  }}
                >
                  키를 입력하고
                </Typography.Text>{' '}
              </Flex>
              <Typography.Text
                style={{
                  color: colorTokens.textDescription,
                  margin: 0,
                }}
              >
                새로운 거래처를 만들어보세요.
              </Typography.Text>
            </Flex>
          </Flex>
        }
        filterOption={filterOption as unknown as boolean}
        value={value}
        options={businessEntityLabelOptions}
        optionFilterProp="label"
        placeholder="거래처, 사업자등록번호, 대표자, 담당자를 검색하세요"
        suffixIcon={<KeyboardArrowDown width={18} height={18} fill={Colors.gray700} />}
        {...selectProps}
        className={cx(
          css`
            font-size: ${theme.typography.body1.size}px;
            line-height: ${theme.typography.body1.lineHeight}px;
          `,
          selectProps?.className,
        )}
        dropdownRender={(menu) => (
          <>
            {menu}

            <Flex
              align="center"
              justify="center"
              gap={4}
              style={{
                borderTop: `1px solid ${colors.grayscale200}`,
              }}
            >
              <Flex
                justify="center"
                gap={8}
                style={{
                  padding: '4px 0',
                }}
                onClick={() => {
                  overlays.open(({ isOpen, onClose }) => (
                    <BusinessEntityCreateModal
                      open={isOpen}
                      onCancel={onClose}
                      onCreate={() => {
                        refetchBusinessEntity();
                      }}
                    />
                  ));
                }}
              >
                <Button icon={<PlusOutlined />}>거래처 등록</Button>
              </Flex>
              <Button
                icon={<UndoOutlined />}
                onClick={() => {
                  onChange(undefined, '');
                }}
              >
                초기화
              </Button>
            </Flex>
          </>
        )}
      />
    </>
  );
};

export const BusinessEntitiesMultiSelect = ({
  onChange,
  value,
  defaultOptions,
  selectProps,
}: {
  onChange: (value: { value: number; label: string }[]) => void;
  value?: number[];
  defaultOptions: {
    label: string;
    value: number;
  }[];
  selectProps?: Omit<SelectProps, 'value' | 'onChange' | 'onSearch'>;
}) => {
  const [search, setSearch] = useState('');
  const session = useSuperUserHook();
  const { companyId } = useDemoAwareCompanyId();
  const debouncedSearch = useDebounce(search, 100);
  const { data } = useBusinessEntitySearchQuery({
    companyId: companyId as string,
    searchParam: debouncedSearch,
  });

  const { data: businessEntityData } = useBusinessEntityListQuery({
    companyId: companyId as string,
    favoriteOnly: false,
  });

  const filterOption = (
    input: string,
    option?: { label: string; value: string; data?: any },
  ): boolean => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  const businessEntityLabelOptions =
    businessEntityData?.Data?.data?.map((b) => ({
      label: b.name,
      value: b.id,
      data: b,
    })) || [];

  return (
    <Select
      {...selectProps}
      mode="multiple"
      style={{ width: 'max-content', minWidth: 300, ...selectProps?.style }}
      showSearch
      value={value}
      onChange={(_, option) => {
        onChange(option as { value: number; label: string }[]);
      }}
      filterOption={filterOption as unknown as boolean}
      options={businessEntityLabelOptions}
      optionFilterProp="label"
      placeholder="거래처, 사업자등록번호, 대표자, 담당자를 검색하세요"
      suffixIcon={<SearchOutlined />}
    />
  );
};

export default SelectBusinessEntity;
